// The account menu's foot: the theme, language and the parts' settings one
// changes on the spot, without going to the settings. The same values as the
// settings' rows (Appearance's theme, General's language).
import { type Locale, m } from "@trameria/i18n";
import type { ReactNode } from "react";
import { useMeApp } from "../app/context";
import { useDeviceSetting } from "../device/device-setting";
import { themeSetting } from "../device/settings";
import { syncAccountLocale } from "../i18n/locale";
import { useProfile, useUpdateProfile } from "../me/profile";
import { Choices } from "../ui/bits";
import { Icon, type IconKind } from "../ui/icon";

export function AccountQuickSettings() {
  const app = useMeApp();
  const [theme, setTheme] = useDeviceSetting(themeSetting);
  const profile = useProfile();
  const update = useUpdateProfile();
  const saved = profile.data;
  return (
    <div className="flex flex-col pb-0.5">
      {app.parts.map((part) => {
        const Quick = part.AccountQuickSettings;
        return Quick ? <Quick key={part.id} /> : null;
      })}
      <QuickRow icon="appearance" label={m.settings_appearance()}>
        <Choices
          label={m.settings_theme()}
          value={theme}
          onChange={setTheme}
          options={[
            { value: "auto", label: m.settings_theme_auto() },
            { value: "light", label: m.settings_theme_light() },
            { value: "dark", label: m.settings_theme_dark() },
          ]}
        />
      </QuickRow>
      <QuickRow icon="globe" label={m.settings_language()}>
        <Choices<"auto" | Locale>
          label={m.settings_language()}
          value={saved?.locale ?? "auto"}
          disabled={!saved || update.isPending}
          options={[
            { value: "auto", label: m.shell_language_auto() },
            // i18n-ignore: a language's name is written in that language
            { value: "ja", label: "日本語" },
            { value: "en", label: "English" },
          ]}
          onChange={(v) => {
            const locale = v === "auto" ? null : v;
            update.mutate(
              { locale },
              { onSuccess: () => syncAccountLocale(locale) },
            );
          }}
        />
      </QuickRow>
    </div>
  );
}

export function QuickRow({
  icon,
  label,
  children,
}: {
  icon: IconKind;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex h-9 items-center gap-2.5 pr-1.5 pl-3 text-[13px] text-body">
      <Icon kind={icon} className="text-muted" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {/* One width for both rows, the segments sharing it: the two controls
          line up edge to edge and segment to segment. */}
      <div className="flex w-[180px] flex-none items-center justify-end gap-2 [&>[role=radiogroup]]:w-full [&_[role=radio]]:flex-1 [&_[role=radio]]:px-1.5">
        {children}
      </div>
    </div>
  );
}
